import { createDraftSafeSelector } from '@reduxjs/toolkit';
import { internalSelectors } from 'state/internal.selectors';
import { sortAuctions } from './filters';
import { isValidType } from './helpers';

const selectSelf = (state: RootState) => state.auctions;
const pathnameSelector = (state: RootState) => state.router.location?.pathname || '';

const rawAuctionsSelector = createDraftSafeSelector(selectSelf, (state) => state.rawData.auctions);
const auctionsSelector = createDraftSafeSelector(selectSelf, (state) => state.data.auctions);
const auctionsSortSelector = createDraftSafeSelector(selectSelf, (state) => state.sort);
const suggestedAuctionsSelector = createDraftSafeSelector(selectSelf, (state) => state.suggested);
const viewedAuctionsSelector = createDraftSafeSelector(selectSelf, (state) => state.viewed);
const trendingAuctionsSelector = createDraftSafeSelector(selectSelf, (state) => state.trending);
const promotedAuctionsSelector = createDraftSafeSelector(selectSelf, (state) => state.promoted);

const auctionsListsSelector = createDraftSafeSelector(
  suggestedAuctionsSelector,
  viewedAuctionsSelector,
  trendingAuctionsSelector,
  promotedAuctionsSelector,
  (suggested, viewed, trending, promoted) => ({ suggested, viewed, trending, promoted }),
);

const auctionsBidDataSelector = (state: RootState) => state.auctionsBids.auctionsBidData;
const auctionsRapDataSelector = (state: RootState) => state.auctionsBids.auctionsRapData;

const auctionsSortForTypeSelector = createDraftSafeSelector(
  auctionsSortSelector,
  pathnameSelector,
  (sort, pathname) => {
    const type = pathname.replace('/auctions/', '') as AuctionsTypes.AuctionTypes;

    const sortForType = sort[type];

    return sortForType;
  },
);

const isSuggestedFilterActive = createDraftSafeSelector(
  selectSelf,
  pathnameSelector,
  (state, pathname) => {
    const type = pathname.replace('/auctions/', '') as AuctionsTypes.AuctionTypes;

    const filters = state.quickFilters[type];
    const pFilters = state.personalizedFilters[type];
    if (!filters || !pFilters) {
      return false;
    }
    const suggestedFilter = [...filters, ...pFilters].find(
      (filter: AuctionsTypes.QuickFiltersType | AuctionsTypes.PersonalizedFiltersType) =>
        filter.name === 'suggested',
    );

    return suggestedFilter?.value || false;
  },
);

const auctionsListSelector = createDraftSafeSelector(
  auctionsSelector,
  auctionsSortForTypeSelector,
  auctionsBidDataSelector,
  auctionsRapDataSelector,
  (state: RootState) => state.attachments.attachments,
  internalSelectors.auctionsWatchSelector,
  isSuggestedFilterActive,
  auctionsListsSelector,
  (
    auctions,
    sortForType,
    bidsMap,
    rapMap,
    attachmentsMap,
    watch,
    isSuggestedFilterActive,
    auctionsLists,
  ) => {
    if (!sortForType || !auctions) {
      return [];
    }

    const { suggested, viewed, trending, promoted } = auctionsLists;

    const auctionsWithBids = auctions.map((auction: AuctionsTypes.AuctionType) => {
      const bidsData = bidsMap[auction.auction_id];
      const rapPrice = rapMap[auction.auction_id];
      const attachments = attachmentsMap[auction.auction_id];

      return {
        active: true,
        ...auction,
        ...bidsData,
        rapPrice,
        attachments,
        watch: watch[auction.auction_id],
        suggested: suggested.includes(auction.auction_id),
        viewed: viewed.includes(auction.auction_id),
        trending: trending.includes(auction.auction_id),
        promoted: promoted.includes(auction.auction_id),
      };
    });

    const sortedAuctions = sortAuctions(sortForType, auctionsWithBids);
    if (isSuggestedFilterActive && suggested.length > 0) {
      sortedAuctions.sort(
        (a, b) => suggested.indexOf(a.auction_id) - suggested.indexOf(b.auction_id),
      );
    }

    return sortedAuctions;
  },
);

const promotedAuctionsListSelector = createDraftSafeSelector(auctionsListSelector, (auctions) => {
  const promoted = auctions.filter((a) => a.promoted);
  const unsorted = promoted.sort((a, b) => a.auction_id - b.auction_id);

  return unsorted;
});

const auctionsNumberListSelector = createDraftSafeSelector(
  auctionsListSelector,
  promotedAuctionsListSelector,
  (auctions, promoted) => {
    const notPromoted = auctions.filter((a) => !a.promoted);
    return [...promoted, ...notPromoted].map((auction) => auction.auction_number);
  },
);

const auctionsIsFilteredSelector = createDraftSafeSelector(selectSelf, (state) => state.isFiltered);
const useSplitApis = createDraftSafeSelector(selectSelf, (state) => state.useSplitApis);

const filteredAuctionsSelector = createDraftSafeSelector(
  selectSelf,
  (state) => state.filteredAuctions,
);

const filtersForTypeSelector = createDraftSafeSelector(
  selectSelf,
  pathnameSelector,
  (auctionsState, pathname) => {
    const type = pathname.replace('/auctions/', '') as AuctionsTypes.AuctionTypes;

    if (isValidType(type)) {
      return auctionsState.filters[type];
    }

    return [];
  },
);

const quickFiltersForTypeSelector = createDraftSafeSelector(
  selectSelf,
  pathnameSelector,
  (auctionsState, pathname) => {
    const type = pathname.replace('/auctions/', '') as AuctionsTypes.AuctionTypes;

    if (isValidType(type)) {
      return auctionsState.quickFilters[type];
    }

    return [];
  },
);

const personalizedFiltersForTypeSelector = createDraftSafeSelector(
  selectSelf,
  pathnameSelector,
  auctionsListSelector,
  (auctionsState, pathname, auctions) => {
    const type = pathname.replace('/auctions/', '') as AuctionsTypes.AuctionTypes;

    if (isValidType(type)) {
      return auctionsState.personalizedFilters[type].map((filter) => {
        if (filter.name === 'watch') {
          return {
            ...filter,
            isDisabled: !auctions.some((auction) => auction.watch),
          };
        }

        if (filter.name === 'suggested') {
          return {
            ...filter,
            isDisabled: !auctions.some((auction) => auction.suggested),
            hidden: auctionsState.suggested.length === 0,
          };
        }

        if (filter.name === 'notViewed') {
          return {
            ...filter,
            isDisabled: !auctions.some((auction) => !auction.viewed),
          };
        }

        if (filter.name === 'trending') {
          return {
            ...filter,
            isDisabled: !auctions.some((auction) => auction.trending),
          };
        }

        return filter;
      });
    }

    return [];
  },
);

const searchForTypeSelector = createDraftSafeSelector(
  selectSelf,
  pathnameSelector,
  (auctionsState, pathname) => {
    const type = pathname.replace('/auctions/', '') as AuctionsTypes.AuctionTypes;

    if (isValidType(type)) {
      return auctionsState.search[type];
    }

    return '';
  },
);

const sortForTypeSelector = createDraftSafeSelector(
  selectSelf,
  pathnameSelector,
  (auctionsState, pathname) => {
    const type = pathname.replace('/auctions/', '') as AuctionsTypes.AuctionTypes;

    if (isValidType(type)) {
      return auctionsState.sort[type];
    }

    return { col: '', asc: true };
  },
);

const attachmentSelector = createDraftSafeSelector(selectSelf, (auctionsState) => {
  const {
    attachment: { attachments: cachedAttachments, isShow, type, selectedAttachment },
  } = auctionsState;

  if (!cachedAttachments || cachedAttachments === null || !type) {
    return {
      isShow: false,
      attachments: null,
    };
  }

  if (type === 'certificates') {
    return {
      isShow,
      attachments: cachedAttachments[type],
    };
  }

  const attachments = [...cachedAttachments['images'], ...cachedAttachments['video360']];

  return {
    isShow,
    selectedAttachment: type === 'video360' ? attachments.length - 1 : selectedAttachment ?? 0,
    attachments,
  };
});

const attachmentImagePreviewSelector = createDraftSafeSelector(selectSelf, (auctionsState) => {
  const {
    attachment: { attachments: cachedAttachments, auctionPreviewImageId },
  } = auctionsState;

  if (!cachedAttachments || cachedAttachments === null) {
    return {
      auctionPreviewImageId: undefined,
    };
  }

  const previewImage =
    cachedAttachments['images'].length > 0 ? cachedAttachments['images'][0].url : undefined;

  return {
    auctionPreviewImageId,
    previewImage,
  };
});

const hasPromotedAuctionsSelector = createDraftSafeSelector(
  rawAuctionsSelector,
  auctionsListsSelector,
  (auctions, auctionsLists) => {
    const { promoted } = auctionsLists;

    return !auctions ? false : auctions?.some((auction) => promoted.includes(auction.auction_id));
  },
);

export const auctionsSelectors = {
  auctionsSelector,
  auctionsSortSelector,
  suggestedAuctionsSelector,
  auctionsSortForTypeSelector,
  auctionsListSelector,
  promotedAuctionsListSelector,
  auctionsNumberListSelector,
  auctionsIsFilteredSelector,
  useSplitApis,
  filteredAuctionsSelector,
  filtersForTypeSelector,
  quickFiltersForTypeSelector,
  personalizedFiltersForTypeSelector,
  searchForTypeSelector,
  sortForTypeSelector,
  attachmentSelector,
  attachmentImagePreviewSelector,
  isSuggestedFilterActive,
  hasPromotedAuctionsSelector,
};
